<template>
  <q-dialog v-model="show">
    <div class="action-bar">
      <q-input
        v-model.trim="search"
        filled
        clearable
        bg-color="white"
        clear-icon="close"
        autofocus
        placeholder="Search command"
        @keyup.enter="executeCommand"
      />

      <q-separator />

      <q-list>
        <template v-for="cmd of filteredCommands">
          <q-item-label :key="cmd.id" header>{{ cmd.title }}</q-item-label>

          <q-item
            v-for="action of cmd.actions"
            :key="`${cmd.id}-item-${action.id}`"
            class="action-bar-command"
            clickable
            tabindex="-1"
            @click="action.exec()"
          >
            <q-item-section :key="`avatar${action.id}`" avatar>
              <q-icon color="primary" :class="action.icon" />
            </q-item-section>

            <q-item-section :key="`label${action.id}`">
              <q-item-label>{{ action.title }}</q-item-label>
              <q-item-label caption lines="1">{{ action.desc }}</q-item-label>
            </q-item-section>

            <q-item-section side>
              <div class="text-caption">Short search</div>
              <kbd>{{ action.shortcut }}</kbd>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </div>
  </q-dialog>
</template>

<script>
import { commands } from './commands'
// Libraries
import hotkeys from 'hotkeys-js'

export default {
  data: () => ({
    search: '',
    show: false
  }),

  computed: {
    filteredCommands() {
      if (!this.search) return this.commands
      const searchTxt = this.search.toLowerCase()

      return this.commands.reduce((acc, curr) => {
        const filteredActions = curr.actions.filter(a => {
          const title = a.title.toLowerCase()
          const shortcut = a.shortcut.toLowerCase()
          return title.includes(searchTxt) || shortcut.includes(searchTxt)
        })
        if (!filteredActions.length) return acc
        acc.push({
          ...curr,
          actions: filteredActions
        })

        return acc
      }, [])
    }
  },

  watch: {
    show: 'arrowsNavigationListeners',
    search: 'setCommandsIndex'
  },

  created() {
    hotkeys('ctrl+k', event => {
      event.preventDefault()
      this.show = true
    })
    this.commands = commands.call(this)
    this.activeIndex = null
    this.actionsLength = null
    this.activeEl = null
    this.activeClassName = 'q-manual-focusable--focused'
  },

  methods: {
    async setCommandsIndex() {
      await this.$nextTick()
      const elements = document.querySelectorAll('.action-bar-command')

      if (this.activeEl) {
        // reset active
        this.activeEl.setAttribute('aria-selected', 'false')
        this.activeEl.classList.remove(this.activeClassName)
      }
      elements.forEach((el, index) => {
        if (index === 0) {
          el.setAttribute('aria-selected', 'true') // set active the first action
          el.classList.add(this.activeClassName)
          this.activeEl = el
        }
        el.setAttribute('data-cmd-i', `${index}`)
      })
      this.activeIndex = 0
      this.actionsLength = elements.length
    },

    navigate(route) {
      this.$router.push(route)
    },

    arrowsNavigationListeners(show) {
      if (show) {
        document.addEventListener('keydown', this.changeActiveAction, true)
        this.setCommandsIndex()
      } else {
        document.removeEventListener('keydown', this.changeActiveAction, true)
        this.search = ''
      }
    },

    changeActiveAction(event) {
      const itsNotDesiredEvent = !['ArrowDown', 'ArrowUp'].includes(event.key)
      const cantChangeActiveIndex =
        (event.key === 'ArrowUp' && this.activeIndex <= 0) ||
        (event.key === 'ArrowDown' && this.activeIndex >= this.actionsLength - 1)

      /* if arrowUp or arrowDown, preventDefault so the search is not affected by the arrows */
      if (!itsNotDesiredEvent) event.preventDefault()
      if (itsNotDesiredEvent || cantChangeActiveIndex) return

      if (event.key === 'ArrowDown') this.activeIndex++
      else this.activeIndex--
      this.changeActiveEl(this.activeIndex)
      this.scrollToMakeItemVisible()
    },

    changeActiveEl(index) {
      this.activeEl.setAttribute('aria-selected', 'false')
      this.activeEl.classList.remove(this.activeClassName)
      const newEl = document.querySelector(`[data-cmd-i="${index}"]`)
      newEl.setAttribute('aria-selected', 'true')
      newEl.classList.add(this.activeClassName)
      this.activeEl = newEl
    },

    scrollToMakeItemVisible() {
      document.querySelector('.q-item[aria-selected="true"]').scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      })
    },

    executeCommand() {
      if (this.actionsLength) this.activeEl.click()
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'
@import '~@/styles/stylus/mixins'

styledScrollFor('.q-list')

.q-list {
  border-radius unset
  border unset
  max-height 400px
  overflow auto

  .q-item {
    padding 15px
  }
}

.action-bar {
  width 550px
  background white

  kbd {
    padding 2px 5px
    border 1px solid $primary
  }

  .content {
    height 350px
  }
}
</style>
